"use client";

import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { newsApi } from "@/api/news";
import { FeedNewsItem, NewsFeed } from "@/types/NewsTypes";
import NewsModal from "./NewsModal";

interface NewsGatesProps {
  /** When false, the gates skip the feed fetch (e.g. on public routes or before auth). */
  enabled: boolean;
}

/**
 * Layout-level orchestrator for post-login news.
 *
 * Single source of truth for the feed:
 *   - Fetches /v1/news/feed once after auth ready.
 *   - Sequentially shows queued pop-ups (records a view per item).
 *   - If a blocking news is present, shows it as a full-screen modal until
 *     the user acknowledges (acknowledged_at set server-side).
 */
export default function NewsGates({ enabled }: NewsGatesProps) {
  const { i18n } = useTranslation("common");
  const [popups, setPopups] = useState<FeedNewsItem[]>([]);
  const [blocking, setBlocking] = useState<FeedNewsItem | null>(null);
  const [popupIndex, setPopupIndex] = useState(0);
  const viewRecorded = useRef<Set<number>>(new Set());

  // Tracks popup ids the user has already dismissed in THIS tab session.
  // Survives the language-switch refetch so dismissed popups don't reappear
  // just because the backend still returns them (the server-side lifetime cap
  // is the long-term enforcement; this ref handles the short-term in-tab UX).
  // Cleared only when `enabled` flips false (logout / leaving authed routes).
  const dismissedIds = useRef<Set<number>>(new Set());

  useEffect(() => {
    if (!enabled) {
      setPopups([]);
      setBlocking(null);
      setPopupIndex(0);
      viewRecorded.current = new Set();
      dismissedIds.current = new Set();
      return;
    }

    let cancelled = false;
    newsApi
      .getFeed(i18n.language)
      .then(response => {
        if (cancelled) return;
        const feed: NewsFeed = response.data ?? { dashboard: [], popups: [], blocking: [] };
        // Filter out anything the user already dismissed in this tab so the
        // freshly-localised feed doesn't resurrect closed popups.
        const fresh = (feed.popups || []).filter(p => !dismissedIds.current.has(p.id));
        setPopups(fresh);
        setBlocking((feed.blocking && feed.blocking[0]) || null);
        setPopupIndex(0);
      })
      .catch(() => {
        if (cancelled) return;
        setPopups([]);
        setBlocking(null);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, i18n.language]);

  const currentPopup = popups[popupIndex];

  // Record a view for the currently-displayed popup (idempotent per id).
  useEffect(() => {
    if (!currentPopup) return;
    if (viewRecorded.current.has(currentPopup.id)) return;
    viewRecorded.current.add(currentPopup.id);
    newsApi.recordView(currentPopup.id).catch(() => {
      /* best-effort; ignore */
    });
  }, [currentPopup]);

  const advancePopup = () => {
    if (currentPopup) {
      dismissedIds.current.add(currentPopup.id);
    }
    setPopupIndex(i => i + 1);
  };

  const handleBlockingAccept = () => {
    if (!blocking) return;
    const id = blocking.id;
    setBlocking(null);
    newsApi.acknowledge(id).catch(() => {
      /* best-effort; next /feed will re-include if this failed */
    });
  };

  return (
    <>
      {currentPopup && (
        <NewsModal
          key={currentPopup.id}
          item={currentPopup}
          mode="popup"
          onAccept={advancePopup}
          onClose={advancePopup}
        />
      )}
      {blocking && (
        <NewsModal
          key={`blocking-${blocking.id}`}
          item={blocking}
          mode="blocking"
          onAccept={handleBlockingAccept}
        />
      )}
    </>
  );
}
